/* RestoreAction.js */

"use strict";
const BackupTypeComponent = require("./veils/type/BackupTypeComponent");
const BackupPreviewComponent = require("./veils/preview/BackupsPreviewComponent");
const ScheduleVeilComponent = require("../../../../lib/actions/veils/schedule/ScheduleVeilComponent");
const RestoreSummaryComponent = require("./veils/restoresummary/RestoreSummaryComponent");

class RestoreAction {
    constructor(
        logger,
        gameEditor,
        activeView,
        DeviceEntity,
        userFeedback,
        userSession,
        jnibAdapter
    ) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._activeView = activeView;
        this._modalUserFeedback = userFeedback;
        this._DeviceEntity = DeviceEntity;
        this._userSession = userSession;
        this._selectedDeviceIds = [];

        this._jnibAdapter = jnibAdapter;
        this._nibApiService = this._jnibAdapter.getNibApiService();

        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedDeviceIds = this._activeView.getSelectedDeviceIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
    }

    _getFilteredDevices(devices, supportedOS) {
        const supportedDevices = [];
        const unsupportedDevices = devices.filter((device) => {
            if (!supportedOS.includes(device.detectedOs)) {
                return true;
            } else {
                supportedDevices.push(device);
            }
        });

        return {supportedDevices, unsupportedDevices};
    }

    _createList(array) {
        const uniqueSet = new Set(array);
        const listItems = [...uniqueSet].map((item) => item || "Empty OS");
        return listItems.join("\n");
    }

    async displayTypeVeil(devices, supportedDevices, unsupportedDevices) {
        const data = await this._modalUserFeedback.displayCustom(
            new BackupTypeComponent(
                this._logger,
                this._userSession,
                this._activeView,
                devices,
                supportedDevices,
                unsupportedDevices,
                this._DeviceEntity,
                this._modalUserFeedback,
                this._gameEditor._supportedOsList,
                this._selection
            ),
            {dontHide: true}
        );
        return data;
    }

    async displayPreviewVeil(devices, supportedDevices, unsupportedDevices, backupsList) {
        const data = await this._modalUserFeedback.displayCustom(
            new BackupPreviewComponent(
                this._logger,
                this._userSession,
                this._activeView,
                devices,
                supportedDevices,
                unsupportedDevices,
                this._DeviceEntity,
                this._modalUserFeedback,
                this._gameEditor._supportedOsList,
                this._selection,
                backupsList
            ),
            {dontHide: true}
        );
        return data;
    }

    async getBackupsForSelection(supportedDevices, selection) {
        let query = {};
        const sort = {capturedTime: -1};
        if (selection && selection.filter) {
            if (selection.filter === "nameMatch") {
                query = {description: {$regex: selection.value, $options: "i"}};
            }
            if (selection.filter === "dateRange") {
                const start = new Date(selection.value.start).getTime();
                const end = new Date(selection.value.end).getTime();
                query = {capturedTime: {$gte: start, $lte: end}};
            }
        }
        let backupsList = await supportedDevices.reduce(async (acc, device) => {
            const backups = await device.loadRelatedDeviceBackupConfigurations(query, {}, sort, {
                limit: 5,
                skip: 0,
            });
            acc[device.id] = backups;
            return acc;
        }, {});

        return backupsList;
    }

    getNoDeviceHasBackups(supportedDevices, backupsList) {
        let noDeviceHasBackups = true;
        noDeviceHasBackups = !supportedDevices.some((device) => {
            return backupsList[device.id].length > 0;
        });
        return noDeviceHasBackups;
    }

    async getFilterSelections(devices, supportedDevices, unsupportedDevices) {
        const selections = await this.displayTypeVeil(
            devices,
            supportedDevices,
            unsupportedDevices
        );
        this._selection = selections;
        // await this._modalUserFeedback.hideDisplayed();
        this._modalUserFeedback.displayLoading();
        let backupsList = await this.getBackupsForSelection(supportedDevices, selections);
        // await this._modalUserFeedback.hideDisplayed();
        let noDeviceHasBackups = this.getNoDeviceHasBackups(supportedDevices, backupsList);
        if (noDeviceHasBackups) {
            try {
                await this._modalUserFeedback.displayError({
                    title: "Restore Error",
                    errorCode: "",
                    errorMsg: `Devices have no backups for the filter criteria. Please try again.`,
                });
            } catch (err) {
                await this._modalUserFeedback.hideDisplayed();
                backupsList = await this.getFilterSelections(
                    devices,
                    supportedDevices,
                    unsupportedDevices
                );
            }
        }
        return backupsList;
    }

    async getBackupsSelection(devices, supportedDevices, unsupportedDevices) {
        let backupsList = await this.getFilterSelections(
            devices,
            supportedDevices,
            unsupportedDevices
        );
        const previewData = await this.displayPreviewVeil(
            devices,
            supportedDevices,
            unsupportedDevices,
            backupsList
        );
        if (previewData && previewData.action === "gotoSelection") {
            return await this.getBackupsSelection(devices, supportedDevices, unsupportedDevices);
        }
        return previewData;
    }

    async run() {
        // Solution needs to be loaded first, else we ignore this action and let the user know
        try {
            const exists = await this._nibApiService.doesRefExist({
                orgId: this._userSession.activeOrganization.id,
                ref: "sys/jnib/features/deviceDetect/discoverDeviceJob.jnib",
            });
            if (exists) {
                try {
                    if (this._selectedDeviceIds.length > 50) {
                        throw {
                            title: "Restore Error",
                            errorCode: "",
                            errorMsg:
                                "A maximum of 50 devices can be restored at one time. Please reduce the number of devices selected and try again.",
                        };
                    }
                    const query = {
                        id: {$in: this._selectedDeviceIds},
                    };

                    const devices = await this._activeView._DeviceEntityDef.loadDevices(
                        this._activeView.activeOrgId,
                        query,
                        {
                            id: true,
                            name: true,
                            detectedOs: true,
                            detectedVendor: true,
                            detectedSku: true,
                            detectedTypeName: true,
                            detectedSoftwareVersion: true,
                            detectedSerialNumber: true,
                            connectionInformation: true,
                            typeBaseGroupMap: true,
                            detectedTypeBase: true,
                        }
                    );

                    const {supportedDevices, unsupportedDevices} = this._getFilteredDevices(
                        devices,
                        this._gameEditor._supportedOsList
                    );

                    if (unsupportedDevices.length === devices.length) {
                        const supportedOSList = this._createList(this._gameEditor._supportedOsList);
                        throw {
                            title: "Restore Error",
                            errorCode: "",
                            errorMsg: `Please select devices running <u title='${supportedOSList}'>supported OSes</u> and try again.`,
                        };
                    }

                    let backupsList = await this.getBackupsForSelection(supportedDevices);
                    let noDeviceHasBackups = this.getNoDeviceHasBackups(
                        supportedDevices,
                        backupsList
                    );
                    if (noDeviceHasBackups) {
                        throw {
                            title: "Restore Error",
                            errorCode: "",
                            errorMsg: `Please select devices that have backups and try again.`,
                        };
                    }
                    const data = await this.getBackupsSelection(
                        devices,
                        supportedDevices,
                        unsupportedDevices
                    );
                    const workTarget = {
                        category: "entity",
                        entityType: "Device",
                        method: "multi",
                        ids: supportedDevices.map((device) => device.id),
                    };

                    const workInput = {
                        postCapture: true,
                        deviceBackup: data.selectedBackups,
                        defaultSnapshot: this._selection.setAsDefaultSnapshot,
                        description: "",
                    };
                    const progressCB = (args) => {
                        console.log(args);
                    };
                    if (data.action === "restore") {
                        // call restore work
                        this._modalUserFeedback.displayLoading({
                            message: "Restoring devices...",
                            showHideButton: true,
                        });
                        const res = await this._activeView._DeviceEntityDef.runRestoreWork(
                            this._userSession.activeOrganization.id,
                            workTarget,
                            workInput,
                            {launchAndForget: false},
                            progressCB
                        );

                        if (!this._modalUserFeedback.isStillOpen) {
                            return;
                        }

                        await this._modalUserFeedback.displayCustom(
                            new RestoreSummaryComponent(res, this._logger)
                        );
                    }

                    if (data.action === "schedule") {
                        const titleMsg = "Schedule Restore";

                        const scheduleDef = await this._modalUserFeedback.displayCustom(
                            new ScheduleVeilComponent(this._logger, false, titleMsg, {
                                workType: "restore",
                                soln: undefined,
                                selectedRowsCount: 1,
                            }),
                            {dontHide: false}
                        );
                        let schedule = scheduleDef.schedule;
                        let description = workInput.description;
                        const scheduleInput = Object.assign({}, workInput, scheduleDef.workInput);

                        await this._activeView._DeviceEntityDef.scheduleRestoreWork(
                            this._userSession.activeOrganization.id,
                            "Restore Backup",
                            description,
                            schedule,
                            workTarget,
                            scheduleInput
                        );
                    }

                    return Promise.resolve();
                } catch (err) {
                    if (err && (err.userRejected || err.closed)) {
                        return this._modalUserFeedback.hideDisplayed();
                    } else {
                        this._logger.warn({error: err}, "RestoreAction.run() - Error");
                        return this._modalUserFeedback.displayError(err).catch(() => {
                            return Promise.resolve();
                        });
                    }
                }
            } else {
                this._modalUserFeedback.displayError({
                    errorMsg:
                        "Looks like you don't have the Device Discovery package installed. Go to Solutions Management to install it.",
                    errorCode: "",
                });
                return false;
            }
        } catch (err) {
            this._logger.warn({error: err}, "RestoreAction.run().getNibApiService - Error");
            return this._modalUserFeedback.displayError(err);
        }
    }
}

module.exports = RestoreAction;

RestoreAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.activeView",
    "$teve.entities.Device",
    "$teve.ui.userFeedback",
    "$teve.userSession",
    "$teve.jnibAdapter",
];

RestoreAction.$displayDetails = {
    type: "Action",
    name: "RestoreAction",
    icon: "devicemanager.restore",
    shown: true,
    title: "Restore Backup",
    order: 9,
};
